import { useState, useMemo, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faGlobe, faPlus, faTrash, faCopy, faCheck, faStar,
  faCircleCheck, faCircleXmark, faClock, faArrowRotateRight, faMagnifyingGlass,
} from '@fortawesome/free-solid-svg-icons'
import { useSupabaseQuery, useSupabaseMutation, supabase } from '../../hooks/useSupabase'
import { nudggeFunctionUrl } from '../../lib/config'
import Modal from '../shared/Modal'

// -------------------------------------------------------------------
// Pure helpers
// -------------------------------------------------------------------

// Basic hostname validation — mirrors the server-side CHECK constraint.
function isValidHostname(h) {
  if (!h) return false
  if (h.length > 253) return false
  return /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i.test(h.trim())
}

// Map (verification_status, cert_status) → visual pill config. Phase 0 of
// this scaffolding has only 'pending', 'revoked' in practice; the others
// light up when the provisioner edge functions land.
function pillFor(row) {
  const vs = row.verification_status
  const cs = row.cert_status
  if (vs === 'revoked') return { label: 'Revoked', icon: faCircleXmark, cls: 'bg-gray-100 dark:bg-gray-900/30 text-gray-700 dark:text-gray-300' }
  if (vs === 'failed') return { label: 'Verification failed', icon: faCircleXmark, cls: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' }
  if (cs === 'failed') return { label: 'Cert failed', icon: faCircleXmark, cls: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' }
  if (cs === 'active' && vs === 'verified') return { label: 'Active', icon: faCircleCheck, cls: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' }
  if (cs === 'provisioning') return { label: 'Provisioning cert', icon: faClock, cls: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' }
  if (vs === 'verified') return { label: 'Verified', icon: faCircleCheck, cls: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' }
  return { label: 'Pending DNS', icon: faClock, cls: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' }
}

function formatTime(ts) {
  if (!ts) return '—'
  return new Date(ts).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
  })
}

// -------------------------------------------------------------------
// Main component
// -------------------------------------------------------------------

export default function DomainsTab({ selectedAccount }) {
  const [showAdd, setShowAdd] = useState(false)
  const [viewingDns, setViewingDns] = useState(null)
  const [viewingStatus, setViewingStatus] = useState(null)
  const [deleting, setDeleting] = useState(null)

  const { data: domains, loading, refetch } = useSupabaseQuery(
    'short_url_domains',
    selectedAccount
      ? { eq: { account_id: selectedAccount.id, is_deleted: false }, order: { column: 'created_at', ascending: false } }
      : null
  )

  const { update, loading: mutating } =
    useSupabaseMutation('short_url_domains')
  const [registering, setRegistering] = useState(false)

  // Calls the Domain-Register edge function which talks to Cloudflare-for-SaaS,
  // gets back the real ownership-verification TXT token + CF custom-hostname id,
  // and persists the row. Auth is the operator's existing Supabase session JWT —
  // the function gates against an admin email allowlist server-side.
  const handleAdd = async (hostname) => {
    const lower = hostname.trim().toLowerCase()
    setRegistering(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) throw new Error('Not signed in')
      const res = await fetch(nudggeFunctionUrl('/Domain-Register'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ account_id: selectedAccount.id, hostname: lower }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data?.error || `Register failed (${res.status})`)
      setShowAdd(false)
      refetch()
    } catch (err) {
      alert(`Could not add domain: ${err.message}`)
    } finally {
      setRegistering(false)
    }
  }

  const handleMakePrimary = async (row) => {
    // Unset current primary (if any), then set this one.
    const currentPrimary = (domains || []).find(d => d.is_primary && d.id !== row.id)
    if (currentPrimary) {
      await update(currentPrimary.id, { is_primary: false }, 'id')
    }
    const { error } = await update(row.id, { is_primary: true }, 'id')
    if (!error) refetch()
  }

  const handleDelete = async () => {
    if (!deleting) return
    // The shared softDelete helper hardcodes PascalCase IsDeleted, which is
    // wrong for the snake_case URL-shortener tables. Update the column directly.
    const { error } = await update(deleting.id, { is_deleted: true }, 'id')
    if (error) {
      alert(`Could not delete domain: ${error.message}`)
      return
    }
    setDeleting(null)
    refetch()
  }

  // Calls Domain-Verify, which queries CF and reconciles the row's
  // verification_status / cert_status / activated_at with what CF reports.
  // Operator-driven for now; eventually a 6h cron does this in the background.
  const handleReverify = async (row) => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) throw new Error('Not signed in')
      const res = await fetch(nudggeFunctionUrl('/Domain-Verify'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ hostname: row.hostname }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data?.error || `Verify failed (${res.status})`)
    } catch (err) {
      alert(`Could not re-verify: ${err.message}`)
    } finally {
      refetch()
    }
  }

  if (!selectedAccount) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-12 text-center">
        <FontAwesomeIcon icon={faGlobe} className="h-12 w-12 text-gray-400 dark:text-gray-500 mb-4" />
        <p className="text-gray-500 dark:text-gray-400 text-lg">No account selected</p>
        <p className="text-gray-400 dark:text-gray-500 text-sm mt-1">
          Pick a shortlink account from the Accounts tab to manage its domains.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Manual-verify banner. Replace once a 6h cron drives re-verification. */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 text-sm text-blue-800 dark:text-blue-200">
        <strong>Operator-driven verification.</strong> <em>Add domain</em> registers the hostname with Cloudflare-for-SaaS. Click the <em>Re-verify</em> icon (or <em>Check Cloudflare status</em>) to reconcile the row with CF. A scheduled background re-check is not deployed yet — see <code>.planning/PLAN-SHORTLINK-DOMAIN-BINDING.md</code> §4 Phase 2.
      </div>

      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Custom Domains</h2>
          <p className="text-gray-600 dark:text-gray-400">
            Tenant hostnames for: <span className="font-medium">{selectedAccount.name}</span>
          </p>
        </div>
        <button
          onClick={() => setShowAdd(true)}
          className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition flex items-center space-x-2"
        >
          <FontAwesomeIcon icon={faPlus} className="w-4 h-4" />
          <span>Add Domain</span>
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
          </div>
        ) : !domains || domains.length === 0 ? (
          <div className="text-center py-12 px-6">
            <FontAwesomeIcon icon={faGlobe} className="h-10 w-10 text-gray-400 mb-3" />
            <h3 className="text-sm font-medium text-gray-900 dark:text-white">No domains yet</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Add your first tenant hostname to issue branded short links under it.
            </p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Hostname</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Last check</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {domains.map(row => {
                const pill = pillFor(row)
                return (
                  <tr key={row.id}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="text-sm font-mono text-gray-900 dark:text-white">{row.hostname}</span>
                        {row.is_primary && (
                          <span className="ml-2 px-2 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-200 text-xs font-semibold rounded flex items-center space-x-1">
                            <FontAwesomeIcon icon={faStar} className="w-3 h-3" />
                            <span>Primary</span>
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded text-xs font-medium ${pill.cls}`}>
                        <FontAwesomeIcon icon={pill.icon} className="w-3 h-3" />
                        <span>{pill.label}</span>
                      </span>
                      {row.verification_last_error && (
                        <div className="text-xs text-red-600 dark:text-red-400 mt-1" title={row.verification_last_error}>
                          {row.verification_last_error.slice(0, 60)}…
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatTime(row.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatTime(row.verification_checked_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm space-x-3">
                      <button
                        onClick={() => setViewingDns(row)}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                        title="Show DNS instructions"
                      >
                        <FontAwesomeIcon icon={faCopy} className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleReverify(row)}
                        className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200"
                        title="Re-verify"
                      >
                        <FontAwesomeIcon icon={faArrowRotateRight} className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setViewingStatus(row)}
                        className="text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
                        title="Check Cloudflare status"
                      >
                        <FontAwesomeIcon icon={faMagnifyingGlass} className="w-4 h-4" />
                      </button>
                      {!row.is_primary && (
                        <button
                          onClick={() => handleMakePrimary(row)}
                          disabled={mutating || row.verification_status !== 'verified' || row.cert_status !== 'active'}
                          className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300 disabled:opacity-30 disabled:cursor-not-allowed"
                          title={
                            row.verification_status === 'verified' && row.cert_status === 'active'
                              ? 'Make primary'
                              : 'Primary can only be set on an Active domain'
                          }
                        >
                          <FontAwesomeIcon icon={faStar} className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => setDeleting(row)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        title="Delete"
                      >
                        <FontAwesomeIcon icon={faTrash} className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>

      {showAdd && (
        <AddDomainModal
          onSubmit={handleAdd}
          onCancel={() => setShowAdd(false)}
          loading={registering}
          existingHostnames={new Set((domains || []).map(d => d.hostname))}
        />
      )}

      {viewingDns && (
        <DnsInstructionsModal
          row={viewingDns}
          onClose={() => setViewingDns(null)}
        />
      )}

      {viewingStatus && (
        <StatusModal
          row={viewingStatus}
          onClose={() => setViewingStatus(null)}
        />
      )}

      {deleting && (
        <Modal title="Delete domain" onClose={() => setDeleting(null)} size="sm">
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-400">
              Remove <strong className="font-mono">{deleting.hostname}</strong>?
              Any short links pointing to this host will stop resolving.
              The DNS record on your side should be removed separately.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleting(null)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={mutating}
                className="px-4 py-2 text-white bg-red-500 rounded-lg hover:bg-red-600 disabled:opacity-50"
              >
                {mutating ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}

// -------------------------------------------------------------------
// Add Domain modal
// -------------------------------------------------------------------

function AddDomainModal({ onSubmit, onCancel, loading, existingHostnames }) {
  const [hostname, setHostname] = useState('')
  const [touched, setTouched] = useState(false)

  const normalized = hostname.trim().toLowerCase()
  const valid = isValidHostname(normalized)
  const duplicate = existingHostnames.has(normalized)
  const error = useMemo(() => {
    if (!touched || !normalized) return null
    if (!valid) return 'Hostname must look like example.com or links.acme.com.'
    if (duplicate) return 'That hostname is already registered on this account.'
    return null
  }, [touched, normalized, valid, duplicate])

  const handleSubmit = (e) => {
    e.preventDefault()
    setTouched(true)
    if (!valid || duplicate) return
    onSubmit(normalized)
  }

  return (
    <Modal title="Add custom domain" onClose={onCancel}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Hostname <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={hostname}
            onChange={e => setHostname(e.target.value)}
            onBlur={() => setTouched(true)}
            required
            autoFocus
            className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder="links.acme.com"
          />
          {error ? (
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">{error}</p>
          ) : (
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Apex domains (<code>acme.com</code>) and subdomains are both accepted.
            </p>
          )}
        </div>

        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 text-xs text-amber-800 dark:text-amber-200">
          After saving, you'll be shown two DNS records to create at the hostname's authoritative DNS:
          a <strong>CNAME</strong> (routing) and a <strong>TXT</strong> (ownership proof). Once both are live
          and Cloudflare has issued a cert, the domain flips to <em>Active</em>.
        </div>

        <div className="flex justify-end space-x-3 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading || !normalized || !valid || duplicate}
            className="px-4 py-2 text-white bg-primary-500 rounded-lg hover:bg-primary-600 disabled:opacity-50"
          >
            {loading ? 'Saving…' : 'Add domain'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// -------------------------------------------------------------------
// DNS instructions modal
// -------------------------------------------------------------------

// The CF-for-SaaS "fallback origin" all tenant CNAMEs point at. Rev 5
// plan: this is a CF hostname we configure once per zone; it proxies
// to the Supabase Custom Domain. Edit when ops sets the actual value.
const CF_FALLBACK_ORIGIN = 'fallback-origin.taplylab.com'

function DnsInstructionsModal({ row, onClose }) {
  const [copied, setCopied] = useState(null)
  const copy = (key, val) => {
    navigator.clipboard.writeText(val)
    setCopied(key)
    setTimeout(() => setCopied(null), 1500)
  }
  const verifyName = `_cf-custom-hostname.${row.hostname}`

  return (
    <Modal title={`DNS for ${row.hostname}`} onClose={onClose}>
      <div className="space-y-4 text-sm">
        <p className="text-gray-700 dark:text-gray-300">
          Create the following two records at the authoritative DNS for{' '}
          <code className="font-mono text-indigo-600 dark:text-indigo-400">{row.hostname}</code>.
          Cloudflare-for-SaaS will pick them up and issue a certificate automatically.
        </p>

        <DnsRow
          label="1. CNAME (routing)"
          name={row.hostname}
          type="CNAME"
          value={CF_FALLBACK_ORIGIN}
          copyKey="cname"
          copied={copied}
          onCopy={copy}
        />
        <DnsRow
          label="2. TXT (ownership proof)"
          name={verifyName}
          type="TXT"
          value={row.verification_token}
          copyKey="txt"
          copied={copied}
          onCopy={copy}
        />

        <div className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded p-3 text-xs text-gray-600 dark:text-gray-300">
          <strong>TTL:</strong> whatever the provider defaults to is fine (300s is typical).
          Verification usually completes within a minute after both records propagate.
        </div>

        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  )
}

function DnsRow({ label, name, type, value, copyKey, copied, onCopy }) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-semibold text-gray-700 dark:text-gray-300">{label}</p>
      <div className="grid grid-cols-[auto_1fr_auto] gap-2 items-center text-xs font-mono">
        <span className="px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded uppercase">{type}</span>
        <code className="px-2 py-1 bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200 rounded truncate" title={name}>{name}</code>
        <button
          onClick={() => onCopy(copyKey + ':name', name)}
          className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          title="Copy name"
        >
          <FontAwesomeIcon icon={copied === copyKey + ':name' ? faCheck : faCopy} className="w-3 h-3" />
        </button>
      </div>
      <div className="grid grid-cols-[auto_1fr_auto] gap-2 items-center text-xs font-mono">
        <span className="px-2 py-1 bg-transparent"></span>
        <code className="px-2 py-1 bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200 rounded break-all">{value}</code>
        <button
          onClick={() => onCopy(copyKey + ':value', value)}
          className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          title="Copy value"
        >
          <FontAwesomeIcon icon={copied === copyKey + ':value' ? faCheck : faCopy} className="w-3 h-3" />
        </button>
      </div>
    </div>
  )
}

// -------------------------------------------------------------------
// Cloudflare status modal
// -------------------------------------------------------------------

// Pretty-prints CF's verification + ssl status for a single custom hostname.
// Calls Domain-Status (which calls CF server-side using stored secrets) so
// the operator never needs the CF dashboard or token to see what's going on.
function StatusModal({ row, onClose }) {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  const [showRaw, setShowRaw] = useState(false)

  const fetchStatus = async () => {
    setLoading(true)
    setError(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) throw new Error('Not signed in')
      const res = await fetch(nudggeFunctionUrl('/Domain-Status'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ hostname: row.hostname }),
      })
      const body = await res.json()
      if (!res.ok) throw new Error(body?.error || `Status fetch failed (${res.status})`)
      setData(body)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchStatus() }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Modal title={`Cloudflare status for ${row.hostname}`} onClose={onClose}>
      <div className="space-y-4 text-sm">
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
          </div>
        )}

        {!loading && error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3 text-red-700 dark:text-red-200">
            <strong>Failed:</strong> {error}
          </div>
        )}

        {!loading && data && !data.found && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded p-3 text-yellow-800 dark:text-yellow-200">
            Cloudflare has no record for <code className="font-mono">{row.hostname}</code>. The DB row exists but the hostname was never registered with CF, or it has been removed there.
          </div>
        )}

        {!loading && data && data.found && (
          <>
            <StatusRow label="Hostname status" value={data.status} kind="status" />
            <StatusRow label="SSL status" value={data.ssl_status} kind="ssl" />

            {data.ownership_verification && (
              <div>
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Ownership TXT challenge</p>
                <div className="bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200 rounded p-2 font-mono text-xs space-y-1">
                  <div><span className="text-gray-500">name:</span> {data.ownership_verification.name}</div>
                  <div className="break-all"><span className="text-gray-500">value:</span> {data.ownership_verification.value}</div>
                </div>
              </div>
            )}

            {data.raw?.ssl?.method === 'txt' && data.raw?.ssl?.txt_name && (
              <div>
                <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 mb-1">
                  SSL validation TXT challenge (extra record needed — legacy method)
                </p>
                <div className="bg-amber-50 dark:bg-amber-900/20 text-amber-900 dark:text-amber-100 rounded p-2 font-mono text-xs space-y-1">
                  <div><span className="opacity-60">name:</span> {data.raw.ssl.txt_name}</div>
                  <div className="break-all"><span className="opacity-60">value:</span> {data.raw.ssl.txt_value}</div>
                </div>
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">
                  This row was registered with the old TXT-validation method. New rows use HTTP-01 which removes this step. To migrate, delete + re-add the domain.
                </p>
              </div>
            )}

            {data.verification_errors?.length > 0 && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3 text-red-700 dark:text-red-200 text-xs">
                <strong>Verification errors:</strong>
                <ul className="list-disc ml-4 mt-1">
                  {data.verification_errors.map((e, i) => <li key={i}>{String(e)}</li>)}
                </ul>
              </div>
            )}

            {data.ssl_validation_errors?.length > 0 && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded p-3 text-red-700 dark:text-red-200 text-xs">
                <strong>SSL validation errors:</strong>
                <ul className="list-disc ml-4 mt-1">
                  {data.ssl_validation_errors.map((e, i) => <li key={i}>{e?.message ?? JSON.stringify(e)}</li>)}
                </ul>
              </div>
            )}

            <div>
              <button
                onClick={() => setShowRaw(s => !s)}
                className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 underline"
              >
                {showRaw ? 'Hide' : 'Show'} raw CF response
              </button>
              {showRaw && (
                <pre className="mt-2 bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200 rounded p-2 text-xs overflow-x-auto max-h-64 overflow-y-auto">
                  {JSON.stringify(data.raw, null, 2)}
                </pre>
              )}
            </div>
          </>
        )}

        <div className="flex justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={fetchStatus}
            disabled={loading}
            className="px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faArrowRotateRight} className="w-3 h-3 mr-1" />
            Refresh
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  )
}

function StatusRow({ label, value, kind }) {
  const tone = (() => {
    if (!value) return 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'
    if (kind === 'status') {
      if (value === 'active') return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
      if (value === 'pending') return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300'
      return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
    }
    if (kind === 'ssl') {
      if (value === 'active') return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
      if (value === 'pending_validation' || value === 'pending_issuance' || value === 'pending_deployment') {
        return 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
      }
      return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
    }
    return 'bg-gray-100 dark:bg-gray-800'
  })()
  return (
    <div className="flex items-center justify-between">
      <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">{label}</span>
      <span className={`px-2 py-1 rounded text-xs font-mono ${tone}`}>{value ?? '—'}</span>
    </div>
  )
}
